import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  api,
  ApiError,
  AvailabilityRule,
  AvailabilityOverride,
  BlockedTime,
  CreateAvailabilityRuleInput,
  CreateAvailabilityOverrideInput,
  CreateBlockedTimeInput,
} from "../lib/api";

const DAYS_OF_WEEK = [
  { value: 0, label: "Sunday" },
  { value: 1, label: "Monday" },
  { value: 2, label: "Tuesday" },
  { value: 3, label: "Wednesday" },
  { value: 4, label: "Thursday" },
  { value: 5, label: "Friday" },
  { value: 6, label: "Saturday" },
];

function formatTime(time: string): string {
  const [hours, minutes] = time.split(":");
  const h = parseInt(hours ?? "0", 10);
  const ampm = h >= 12 ? "PM" : "AM";
  const displayHour = h % 12 || 12;
  return `${displayHour}:${minutes} ${ampm}`;
}

function formatDate(dateStr: string): string {
  const date = new Date(dateStr + "T00:00:00");
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function formatDateTime(isoStr: string): string {
  const date = new Date(isoStr);
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

interface RuleFormProps {
  calendarId: string;
  rule?: AvailabilityRule;
  onClose: () => void;
  onSuccess: () => void;
}

function RuleForm({ calendarId, rule, onClose, onSuccess }: RuleFormProps) {
  const isEdit = !!rule;
  const [dayOfWeek, setDayOfWeek] = useState(rule?.dayOfWeek ?? 1);
  const [startTime, setStartTime] = useState(rule?.startTime ?? "09:00");
  const [endTime, setEndTime] = useState(rule?.endTime ?? "17:00");
  const [error, setError] = useState("");

  const createMutation = useMutation({
    mutationFn: (data: CreateAvailabilityRuleInput) =>
      api.availability.rules.create(data),
    onSuccess: () => {
      onSuccess();
      onClose();
    },
    onError: (err) => {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError("Failed to create rule");
      }
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: Partial<CreateAvailabilityRuleInput>) =>
      api.availability.rules.update(rule!.id, data),
    onSuccess: () => {
      onSuccess();
      onClose();
    },
    onError: (err) => {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError("Failed to update rule");
      }
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (startTime >= endTime) {
      setError("End time must be after start time");
      return;
    }

    const data: CreateAvailabilityRuleInput = {
      calendarId,
      dayOfWeek,
      startTime,
      endTime,
    };

    if (isEdit) {
      updateMutation.mutate({ dayOfWeek, startTime, endTime });
    } else {
      createMutation.mutate(data);
    }
  };

  const isPending = createMutation.isPending || updateMutation.isPending;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
        <h2 className="text-xl font-semibold mb-4">
          {isEdit ? "Edit Weekly Hours" : "Add Weekly Hours"}
        </h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label
              htmlFor="dayOfWeek"
              className="block text-sm font-medium text-gray-700"
            >
              Day of Week
            </label>
            <select
              id="dayOfWeek"
              value={dayOfWeek}
              onChange={(e) => setDayOfWeek(parseInt(e.target.value, 10))}
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-amber-500 focus:outline-none focus:ring-amber-500"
            >
              {DAYS_OF_WEEK.map((day) => (
                <option key={day.value} value={day.value}>
                  {day.label}
                </option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label
                htmlFor="startTime"
                className="block text-sm font-medium text-gray-700"
              >
                Start Time
              </label>
              <input
                id="startTime"
                type="time"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-amber-500 focus:outline-none focus:ring-amber-500"
                required
              />
            </div>
            <div>
              <label
                htmlFor="endTime"
                className="block text-sm font-medium text-gray-700"
              >
                End Time
              </label>
              <input
                id="endTime"
                type="time"
                value={endTime}
                onChange={(e) => setEndTime(e.target.value)}
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-amber-500 focus:outline-none focus:ring-amber-500"
                required
              />
            </div>
          </div>

          {error && (
            <div className="rounded-md bg-red-50 p-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          <div className="flex gap-3 justify-end pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isPending}
              className="px-4 py-2 text-sm font-medium text-white bg-amber-600 rounded-md hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isPending ? "Saving..." : isEdit ? "Save Changes" : "Add Hours"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

interface OverrideFormProps {
  calendarId: string;
  override?: AvailabilityOverride;
  onClose: () => void;
  onSuccess: () => void;
}

function OverrideForm({
  calendarId,
  override,
  onClose,
  onSuccess,
}: OverrideFormProps) {
  const isEdit = !!override;
  const [date, setDate] = useState(override?.date ?? "");
  const [isClosed, setIsClosed] = useState(
    override ? !override.isAvailable : false
  );
  const [startTime, setStartTime] = useState(override?.startTime ?? "09:00");
  const [endTime, setEndTime] = useState(override?.endTime ?? "17:00");
  const [reason, setReason] = useState(override?.reason ?? "");
  const [error, setError] = useState("");

  const createMutation = useMutation({
    mutationFn: (data: CreateAvailabilityOverrideInput) =>
      api.availability.overrides.create(data),
    onSuccess: () => {
      onSuccess();
      onClose();
    },
    onError: (err) => {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError("Failed to create override");
      }
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: Partial<CreateAvailabilityOverrideInput>) =>
      api.availability.overrides.update(override!.id, data),
    onSuccess: () => {
      onSuccess();
      onClose();
    },
    onError: (err) => {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError("Failed to update override");
      }
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!date) {
      setError("Date is required");
      return;
    }

    if (!isClosed && startTime >= endTime) {
      setError("End time must be after start time");
      return;
    }

    const data: CreateAvailabilityOverrideInput = {
      calendarId,
      date,
      isAvailable: !isClosed,
      startTime: isClosed ? null : startTime,
      endTime: isClosed ? null : endTime,
      reason: reason.trim() || null,
    };

    if (isEdit) {
      updateMutation.mutate({
        date,
        isAvailable: !isClosed,
        startTime: isClosed ? null : startTime,
        endTime: isClosed ? null : endTime,
        reason: reason.trim() || null,
      });
    } else {
      createMutation.mutate(data);
    }
  };

  const isPending = createMutation.isPending || updateMutation.isPending;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
        <h2 className="text-xl font-semibold mb-4">
          {isEdit ? "Edit Override" : "Add Date Override"}
        </h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label
              htmlFor="date"
              className="block text-sm font-medium text-gray-700"
            >
              Date
            </label>
            <input
              id="date"
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-amber-500 focus:outline-none focus:ring-amber-500"
              required
            />
          </div>

          <div className="flex items-center">
            <input
              id="isClosed"
              type="checkbox"
              checked={isClosed}
              onChange={(e) => setIsClosed(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-amber-600 focus:ring-amber-500"
            />
            <label htmlFor="isClosed" className="ml-2 text-sm text-gray-700">
              Closed all day
            </label>
          </div>

          {!isClosed && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label
                  htmlFor="overrideStartTime"
                  className="block text-sm font-medium text-gray-700"
                >
                  Start Time
                </label>
                <input
                  id="overrideStartTime"
                  type="time"
                  value={startTime}
                  onChange={(e) => setStartTime(e.target.value)}
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-amber-500 focus:outline-none focus:ring-amber-500"
                  required
                />
              </div>
              <div>
                <label
                  htmlFor="overrideEndTime"
                  className="block text-sm font-medium text-gray-700"
                >
                  End Time
                </label>
                <input
                  id="overrideEndTime"
                  type="time"
                  value={endTime}
                  onChange={(e) => setEndTime(e.target.value)}
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-amber-500 focus:outline-none focus:ring-amber-500"
                  required
                />
              </div>
            </div>
          )}

          <div>
            <label
              htmlFor="reason"
              className="block text-sm font-medium text-gray-700"
            >
              Reason (optional)
            </label>
            <input
              id="reason"
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g., Holiday, Vacation"
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-amber-500 focus:outline-none focus:ring-amber-500"
            />
          </div>

          {error && (
            <div className="rounded-md bg-red-50 p-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          <div className="flex gap-3 justify-end pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isPending}
              className="px-4 py-2 text-sm font-medium text-white bg-amber-600 rounded-md hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isPending
                ? "Saving..."
                : isEdit
                  ? "Save Changes"
                  : "Add Override"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

interface BlockedTimeFormProps {
  calendarId: string;
  blockedTime?: BlockedTime;
  onClose: () => void;
  onSuccess: () => void;
}

function BlockedTimeForm({
  calendarId,
  blockedTime,
  onClose,
  onSuccess,
}: BlockedTimeFormProps) {
  const isEdit = !!blockedTime;

  const getLocalDateTime = (isoStr?: string) => {
    if (!isoStr) return "";
    const date = new Date(isoStr);
    // Format as YYYY-MM-DDTHH:mm for datetime-local input
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  const [title, setTitle] = useState(blockedTime?.reason ?? "");
  const [startTime, setStartTime] = useState(
    getLocalDateTime(blockedTime?.startTime)
  );
  const [endTime, setEndTime] = useState(
    getLocalDateTime(blockedTime?.endTime)
  );
  const [error, setError] = useState("");

  const createMutation = useMutation({
    mutationFn: (data: CreateBlockedTimeInput) =>
      api.availability.blockedTimes.create(data),
    onSuccess: () => {
      onSuccess();
      onClose();
    },
    onError: (err) => {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError("Failed to create blocked time");
      }
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: Partial<CreateBlockedTimeInput>) =>
      api.availability.blockedTimes.update(blockedTime!.id, data),
    onSuccess: () => {
      onSuccess();
      onClose();
    },
    onError: (err) => {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError("Failed to update blocked time");
      }
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!startTime || !endTime) {
      setError("Start and end times are required");
      return;
    }

    const startDate = new Date(startTime);
    const endDate = new Date(endTime);

    if (startDate >= endDate) {
      setError("End time must be after start time");
      return;
    }

    const data: CreateBlockedTimeInput = {
      calendarId,
      startTime: startDate.toISOString(),
      endTime: endDate.toISOString(),
      reason: title.trim() || null,
    };

    if (isEdit) {
      updateMutation.mutate({
        startTime: startDate.toISOString(),
        endTime: endDate.toISOString(),
        reason: title.trim() || null,
      });
    } else {
      createMutation.mutate(data);
    }
  };

  const isPending = createMutation.isPending || updateMutation.isPending;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
        <h2 className="text-xl font-semibold mb-4">
          {isEdit ? "Edit Blocked Time" : "Add Blocked Time"}
        </h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label
              htmlFor="title"
              className="block text-sm font-medium text-gray-700"
            >
              Title (optional)
            </label>
            <input
              id="title"
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g., Lunch Meeting"
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-amber-500 focus:outline-none focus:ring-amber-500"
            />
          </div>

          <div>
            <label
              htmlFor="blockedStartTime"
              className="block text-sm font-medium text-gray-700"
            >
              Start
            </label>
            <input
              id="blockedStartTime"
              type="datetime-local"
              value={startTime}
              onChange={(e) => setStartTime(e.target.value)}
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-amber-500 focus:outline-none focus:ring-amber-500"
              required
            />
          </div>

          <div>
            <label
              htmlFor="blockedEndTime"
              className="block text-sm font-medium text-gray-700"
            >
              End
            </label>
            <input
              id="blockedEndTime"
              type="datetime-local"
              value={endTime}
              onChange={(e) => setEndTime(e.target.value)}
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-amber-500 focus:outline-none focus:ring-amber-500"
              required
            />
          </div>

          {error && (
            <div className="rounded-md bg-red-50 p-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          <div className="flex gap-3 justify-end pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isPending}
              className="px-4 py-2 text-sm font-medium text-white bg-amber-600 rounded-md hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isPending
                ? "Saving..."
                : isEdit
                  ? "Save Changes"
                  : "Add Blocked Time"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

interface DeleteConfirmModalProps {
  title: string;
  message: string;
  onClose: () => void;
  onConfirm: () => void;
  isPending: boolean;
  error?: string;
}

function DeleteConfirmModal({
  title,
  message,
  onClose,
  onConfirm,
  isPending,
  error,
}: DeleteConfirmModalProps) {
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-sm w-full p-6">
        <h2 className="text-xl font-semibold mb-4">{title}</h2>
        <p className="text-gray-600 mb-4">{message}</p>
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
            {error}
          </div>
        )}
        <div className="flex gap-3 justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={isPending}
            className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isPending ? "Deleting..." : "Delete"}
          </button>
        </div>
      </div>
    </div>
  );
}

interface WeeklyHoursSectionProps {
  calendarId: string;
  rules: AvailabilityRule[];
  onSuccess: () => void;
}

function WeeklyHoursSection({
  calendarId,
  rules,
  onSuccess,
}: WeeklyHoursSectionProps) {
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingRule, setEditingRule] = useState<AvailabilityRule | null>(null);
  const [deletingRule, setDeletingRule] = useState<AvailabilityRule | null>(
    null
  );
  const [deleteError, setDeleteError] = useState("");

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.availability.rules.delete(id),
    onSuccess: () => {
      onSuccess();
      setDeletingRule(null);
      setDeleteError("");
    },
    onError: (err) => {
      setDeleteError(
        err instanceof ApiError ? err.message : "Failed to delete rule"
      );
    },
  });

  // Group rules by day of week
  const rulesByDay = DAYS_OF_WEEK.map((day) => ({
    ...day,
    rules: rules.filter((r) => r.dayOfWeek === day.value),
  }));

  return (
    <div className="bg-white shadow rounded-lg p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-gray-900">Weekly Hours</h2>
        <button
          onClick={() => setShowAddModal(true)}
          className="px-3 py-1.5 text-sm font-medium text-amber-600 border border-amber-600 rounded-md hover:bg-amber-50"
        >
          + Add Hours
        </button>
      </div>

      <div className="space-y-2">
        {rulesByDay.map((day) => (
          <div
            key={day.value}
            className="flex items-start py-2 border-b border-gray-100 last:border-0"
          >
            <span className="w-28 text-sm font-medium text-gray-700">
              {day.label}
            </span>
            <div className="flex-1">
              {day.rules.length === 0 ? (
                <span className="text-sm text-gray-400">Not available</span>
              ) : (
                <div className="space-y-1">
                  {day.rules.map((rule) => (
                    <div
                      key={rule.id}
                      className="flex items-center gap-2 text-sm"
                    >
                      <span className="text-gray-600">
                        {formatTime(rule.startTime)} -{" "}
                        {formatTime(rule.endTime)}
                      </span>
                      <button
                        onClick={() => setEditingRule(rule)}
                        className="text-amber-600 hover:text-amber-700 text-xs"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => setDeletingRule(rule)}
                        className="text-red-600 hover:text-red-700 text-xs"
                      >
                        Delete
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {showAddModal && (
        <RuleForm
          calendarId={calendarId}
          onClose={() => setShowAddModal(false)}
          onSuccess={onSuccess}
        />
      )}

      {editingRule && (
        <RuleForm
          calendarId={calendarId}
          rule={editingRule}
          onClose={() => setEditingRule(null)}
          onSuccess={onSuccess}
        />
      )}

      {deletingRule && (
        <DeleteConfirmModal
          title="Delete Hours?"
          message={`Are you sure you want to delete the hours for ${DAYS_OF_WEEK.find((d) => d.value === deletingRule.dayOfWeek)?.label}?`}
          onClose={() => {
            setDeletingRule(null);
            setDeleteError("");
          }}
          onConfirm={() => deleteMutation.mutate(deletingRule.id)}
          isPending={deleteMutation.isPending}
          error={deleteError}
        />
      )}
    </div>
  );
}

interface OverridesSectionProps {
  calendarId: string;
  overrides: AvailabilityOverride[];
  onSuccess: () => void;
}

function OverridesSection({
  calendarId,
  overrides,
  onSuccess,
}: OverridesSectionProps) {
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingOverride, setEditingOverride] =
    useState<AvailabilityOverride | null>(null);
  const [deletingOverride, setDeletingOverride] =
    useState<AvailabilityOverride | null>(null);
  const [deleteError, setDeleteError] = useState("");

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.availability.overrides.delete(id),
    onSuccess: () => {
      onSuccess();
      setDeletingOverride(null);
      setDeleteError("");
    },
    onError: (err) => {
      setDeleteError(
        err instanceof ApiError ? err.message : "Failed to delete override"
      );
    },
  });

  return (
    <div className="bg-white shadow rounded-lg p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-gray-900">Date Overrides</h2>
        <button
          onClick={() => setShowAddModal(true)}
          className="px-3 py-1.5 text-sm font-medium text-amber-600 border border-amber-600 rounded-md hover:bg-amber-50"
        >
          + Add Override
        </button>
      </div>

      {overrides.length === 0 ? (
        <p className="text-sm text-gray-500">
          No date overrides. Add an override to set special hours for specific
          dates.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead>
              <tr>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                  Date
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                  Hours
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                  Reason
                </th>
                <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {overrides.map((override) => (
                <tr key={override.id}>
                  <td className="px-3 py-2 text-sm text-gray-900">
                    {formatDate(override.date)}
                  </td>
                  <td className="px-3 py-2 text-sm">
                    {override.isAvailable ? (
                      <span className="text-gray-600">
                        {formatTime(override.startTime ?? "00:00")} -{" "}
                        {formatTime(override.endTime ?? "00:00")}
                      </span>
                    ) : (
                      <span className="text-red-600">Closed</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-sm text-gray-500">
                    {override.reason ?? "-"}
                  </td>
                  <td className="px-3 py-2 text-sm text-right">
                    <button
                      onClick={() => setEditingOverride(override)}
                      className="text-amber-600 hover:text-amber-700 mr-3"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => setDeletingOverride(override)}
                      className="text-red-600 hover:text-red-700"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showAddModal && (
        <OverrideForm
          calendarId={calendarId}
          onClose={() => setShowAddModal(false)}
          onSuccess={onSuccess}
        />
      )}

      {editingOverride && (
        <OverrideForm
          calendarId={calendarId}
          override={editingOverride}
          onClose={() => setEditingOverride(null)}
          onSuccess={onSuccess}
        />
      )}

      {deletingOverride && (
        <DeleteConfirmModal
          title="Delete Override?"
          message={`Are you sure you want to delete the override for ${formatDate(deletingOverride.date)}?`}
          onClose={() => {
            setDeletingOverride(null);
            setDeleteError("");
          }}
          onConfirm={() => deleteMutation.mutate(deletingOverride.id)}
          isPending={deleteMutation.isPending}
          error={deleteError}
        />
      )}
    </div>
  );
}

interface BlockedTimesSectionProps {
  calendarId: string;
  blockedTimes: BlockedTime[];
  onSuccess: () => void;
}

function BlockedTimesSection({
  calendarId,
  blockedTimes,
  onSuccess,
}: BlockedTimesSectionProps) {
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingBlockedTime, setEditingBlockedTime] =
    useState<BlockedTime | null>(null);
  const [deletingBlockedTime, setDeletingBlockedTime] =
    useState<BlockedTime | null>(null);
  const [deleteError, setDeleteError] = useState("");

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.availability.blockedTimes.delete(id),
    onSuccess: () => {
      onSuccess();
      setDeletingBlockedTime(null);
      setDeleteError("");
    },
    onError: (err) => {
      setDeleteError(
        err instanceof ApiError ? err.message : "Failed to delete blocked time"
      );
    },
  });

  return (
    <div className="bg-white shadow rounded-lg p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-gray-900">Blocked Times</h2>
        <button
          onClick={() => setShowAddModal(true)}
          className="px-3 py-1.5 text-sm font-medium text-amber-600 border border-amber-600 rounded-md hover:bg-amber-50"
        >
          + Add Blocked Time
        </button>
      </div>

      {blockedTimes.length === 0 ? (
        <p className="text-sm text-gray-500">
          No blocked times. Add a blocked time to mark specific time ranges as
          unavailable.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead>
              <tr>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                  Title
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                  Start
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                  End
                </th>
                <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {blockedTimes.map((blocked) => (
                <tr key={blocked.id}>
                  <td className="px-3 py-2 text-sm text-gray-900">
                    {blocked.reason ?? "-"}
                  </td>
                  <td className="px-3 py-2 text-sm text-gray-600">
                    {formatDateTime(blocked.startTime)}
                  </td>
                  <td className="px-3 py-2 text-sm text-gray-600">
                    {formatDateTime(blocked.endTime)}
                  </td>
                  <td className="px-3 py-2 text-sm text-right">
                    <button
                      onClick={() => setEditingBlockedTime(blocked)}
                      className="text-amber-600 hover:text-amber-700 mr-3"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => setDeletingBlockedTime(blocked)}
                      className="text-red-600 hover:text-red-700"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showAddModal && (
        <BlockedTimeForm
          calendarId={calendarId}
          onClose={() => setShowAddModal(false)}
          onSuccess={onSuccess}
        />
      )}

      {editingBlockedTime && (
        <BlockedTimeForm
          calendarId={calendarId}
          blockedTime={editingBlockedTime}
          onClose={() => setEditingBlockedTime(null)}
          onSuccess={onSuccess}
        />
      )}

      {deletingBlockedTime && (
        <DeleteConfirmModal
          title="Delete Blocked Time?"
          message={`Are you sure you want to delete "${deletingBlockedTime.reason ?? "this blocked time"}"?`}
          onClose={() => {
            setDeletingBlockedTime(null);
            setDeleteError("");
          }}
          onConfirm={() => deleteMutation.mutate(deletingBlockedTime.id)}
          isPending={deleteMutation.isPending}
          error={deleteError}
        />
      )}
    </div>
  );
}

export function AvailabilityPage() {
  const [selectedCalendarId, setSelectedCalendarId] = useState<string>("");
  const queryClient = useQueryClient();

  const { data: calendarsData, isLoading: isLoadingCalendars } = useQuery({
    queryKey: ["calendars"],
    queryFn: () => api.calendars.list(),
  });

  // Auto-select first calendar when data loads
  const calendars = calendarsData?.data ?? [];
  if (calendars.length > 0 && !selectedCalendarId) {
    const firstCalendar = calendars[0];
    if (firstCalendar) {
      setSelectedCalendarId(firstCalendar.id);
    }
  }

  const { data: rulesData, isLoading: isLoadingRules } = useQuery({
    queryKey: ["availability", "rules", selectedCalendarId],
    queryFn: () => api.availability.rules.list({ calendarId: selectedCalendarId }),
    enabled: !!selectedCalendarId,
  });

  const { data: overridesData, isLoading: isLoadingOverrides } = useQuery({
    queryKey: ["availability", "overrides", selectedCalendarId],
    queryFn: () =>
      api.availability.overrides.list({ calendarId: selectedCalendarId }),
    enabled: !!selectedCalendarId,
  });

  const { data: blockedTimesData, isLoading: isLoadingBlockedTimes } = useQuery(
    {
      queryKey: ["availability", "blockedTimes", selectedCalendarId],
      queryFn: () =>
        api.availability.blockedTimes.list({ calendarId: selectedCalendarId }),
      enabled: !!selectedCalendarId,
    }
  );

  const handleSuccess = () => {
    queryClient.invalidateQueries({
      queryKey: ["availability"],
    });
  };

  const isLoading =
    isLoadingCalendars ||
    isLoadingRules ||
    isLoadingOverrides ||
    isLoadingBlockedTimes;

  // Note: selectedCalendar could be used for display, but currently just using the dropdown
  const _selectedCalendar = calendars.find((c) => c.id === selectedCalendarId);
  void _selectedCalendar; // Suppress unused warning until we need calendar details

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Availability</h1>
      </div>

      {/* Calendar Selector */}
      <div className="mb-6">
        <label
          htmlFor="calendarSelect"
          className="block text-sm font-medium text-gray-700 mb-1"
        >
          Calendar
        </label>
        <select
          id="calendarSelect"
          value={selectedCalendarId}
          onChange={(e) => setSelectedCalendarId(e.target.value)}
          className="block w-full max-w-xs rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-amber-500 focus:outline-none focus:ring-amber-500"
          disabled={isLoadingCalendars || calendars.length === 0}
        >
          {calendars.length === 0 && !isLoadingCalendars && (
            <option value="">No calendars available</option>
          )}
          {calendars.map((calendar) => (
            <option key={calendar.id} value={calendar.id}>
              {calendar.name}
            </option>
          ))}
        </select>
      </div>

      {isLoadingCalendars ? (
        <div className="text-center py-12">
          <div className="animate-pulse text-gray-500">
            Loading calendars...
          </div>
        </div>
      ) : calendars.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <p className="text-gray-500">
            No calendars available. Create a calendar first to manage
            availability.
          </p>
        </div>
      ) : !selectedCalendarId ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <p className="text-gray-500">Select a calendar to view availability.</p>
        </div>
      ) : isLoading ? (
        <div className="text-center py-12">
          <div className="animate-pulse text-gray-500">
            Loading availability...
          </div>
        </div>
      ) : (
        <div className="space-y-6">
          <WeeklyHoursSection
            calendarId={selectedCalendarId}
            rules={rulesData?.data ?? []}
            onSuccess={handleSuccess}
          />

          <OverridesSection
            calendarId={selectedCalendarId}
            overrides={overridesData?.data ?? []}
            onSuccess={handleSuccess}
          />

          <BlockedTimesSection
            calendarId={selectedCalendarId}
            blockedTimes={blockedTimesData?.data ?? []}
            onSuccess={handleSuccess}
          />
        </div>
      )}
    </div>
  );
}
